// public/v3/src/components/pain-assessment-modal.jsx
// Modal for recording a CPM pain-scale assessment (NRS in P1.1).
//
// Used from Time Logs (Pain view). Touch-first: score field auto-focused,
// numeric keypad on iOS, native date picker, progressive disclosure for
// date + time-spent under an "adjust" link.
//
// Persistence belongs to the parent: onSave receives the validated form so
// the structured pain assessment and activity-log entry land in one PUT.
//
// Unsaved-changes guard covers backdrop, Cancel, X, and Esc dismissal.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { PAIN_SCALES_BUILTIN, lookupPainScale, computePainSeverity, latestPainAssessment } from '../lib/pain-scales';
import { severityCssToken } from '../lib/bhi-assessment-entry';
import { useUnsavedChangesGuard } from '../lib/hooks/use-unsaved-changes-guard';
import { formatLastRecorded } from '../lib/bhi-last-recorded';

function todayYmd() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export function PainAssessmentModal({ patient, onClose, onSave }) {
  // Hooks must remain unconditional; null patient state falls through to the
  // render-time return only after every hook has run.
  const raw = patient?._raw || patient || null;
  const effectiveScales = useMemo(() => PAIN_SCALES_BUILTIN, []);
  const lastAssessment = latestPainAssessment(raw);
  const defaultScale = (lastAssessment && lastAssessment.scale)
    || (effectiveScales[0] && effectiveScales[0].id)
    || 'NRS';

  const [scale, setScale] = useState(defaultScale);
  const [date, setDate] = useState(todayYmd());
  const [score, setScore] = useState('');
  const [notes, setNotes] = useState('');
  const [timeSpent, setTimeSpent] = useState(5);
  const [showAdjust, setShowAdjust] = useState(false);
  const [scaleOpen, setScaleOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const scoreRef = useRef(null);

  useEffect(() => {
    if (scoreRef.current) scoreRef.current.focus();
  }, []);

  const scaleDef = lookupPainScale(scale);
  const maxScore = scaleDef ? scaleDef.maxScore : 10;
  const numericScore = Number(score);
  const scoreValid = score !== '' && Number.isFinite(numericScore) && numericScore >= 0 && numericScore <= maxScore;
  const severity = scoreValid ? computePainSeverity(scale, numericScore) : '';

  const { attemptDismiss, showDiscardPrompt, confirmDiscard, keepEditing } = useUnsavedChangesGuard({
    values: { scale, date, score, notes, timeSpent },
    onClose,
    busy,
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') attemptDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attemptDismiss]);

  if (!patient || !raw) return null;

  async function handleSave(e) {
    if (e) e.preventDefault();
    if (!scoreValid) {
      setError('Score must be a number between 0 and ' + maxScore + '.');
      return;
    }
    // A future-dated assessment would satisfy the month gate for a service
    // not yet rendered — the note must never assert one.
    if (!date || date > todayYmd()) {
      setError('Assessment date must be on or before today.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const form = { scale, date, score: numericScore, severity, notes, timeSpent: Number(timeSpent) || 5 };
      await onSave(form);
      onClose && onClose();
    } catch (err) {
      if (err && err.status === 409) {
        setError('Patient was updated elsewhere. Refresh and try again.');
      } else {
        setError(err && err.message ? err.message : 'Save failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  const patientName = [raw.fname, raw.lname].filter(Boolean).join(' ') || patient.name || patient.id;
  const monthLabel = date.slice(0, 7);
  const lastRecordedLine = formatLastRecorded(lastAssessment);

  return (
    <div className="modal-backdrop" onClick={attemptDismiss}>
      <div
        className="modal bhi-assessment-modal"
        role="dialog"
        aria-modal="true"
        aria-label={showDiscardPrompt ? 'Discard pain assessment' : `Record pain assessment for ${patientName}`}
        onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>{showDiscardPrompt ? 'Discard pain assessment?' : 'Record pain assessment'}</h3>
            {!showDiscardPrompt && (
              <div className="sub">{patientName} · {monthLabel}</div>
            )}
          </div>
          <button className="btn ghost icon" onClick={attemptDismiss} aria-label="Close">✕</button>
        </div>

        {showDiscardPrompt ? (
          <>
            <div className="modal-body">
              <p>You've entered a score but haven't saved yet.</p>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn" onClick={keepEditing}>Keep editing</button>
              <button type="button" className="btn warn" onClick={confirmDiscard}>Discard</button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSave}>
            <div className="modal-body">
              <div className="bhi-last-recorded">{lastRecordedLine || 'No prior pain assessment on file.'}</div>
              <div className="field">
                <div className="bhi-scale-row">
                  <span className="bhi-scale-name">{scaleDef ? scaleDef.name : scale}</span>
                  <button type="button" className="btn ghost sm pill" onClick={() => setScaleOpen((v) => !v)}>
                    change ▾
                  </button>
                </div>
                {scaleOpen && (
                  <select
                    className="bhi-scale-picker"
                    value={scale}
                    onChange={(e) => { setScale(e.target.value); setScaleOpen(false); }}>
                    {effectiveScales.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="field">
                <div className="field-label">Score (0–{maxScore})</div>
                <div className="bhi-score-row">
                  <input
                    ref={scoreRef}
                    type="number"
                    className="score-input tabnum"
                    inputMode="numeric"
                    min={0}
                    max={maxScore}
                    value={score}
                    onChange={(e) => setScore(e.target.value)}
                    disabled={busy} />
                  {severity && (
                    <span className={'severity-badge ' + severityCssToken(severity)}>{severity}</span>
                  )}
                </div>
              </div>

              <div className="field">
                <div className="field-label">Note (optional)</div>
                <textarea
                  className="bhi-notes-input"
                  rows={2}
                  maxLength={240}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={busy}
                  autoCapitalize="sentences"
                  autoCorrect="on" />
              </div>

              <div className="bhi-adjust-caption">
                <span>
                  Logs {timeSpent} min of pain-management time on {date}
                </span>
                <button type="button" className="btn link sm" onClick={() => setShowAdjust((v) => !v)}>
                  {showAdjust ? 'hide' : 'adjust'}
                </button>
              </div>

              {showAdjust && (
                <div className="bhi-adjust-fields">
                  <div className="field">
                    <div className="field-label">Date</div>
                    <input type="date" value={date} max={todayYmd()} onChange={(e) => setDate(e.target.value)} disabled={busy} />
                  </div>
                  <div className="field">
                    <div className="field-label">Time spent (min)</div>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={60}
                      value={timeSpent}
                      onChange={(e) => setTimeSpent(e.target.value)}
                      disabled={busy} />
                  </div>
                </div>
              )}

              {error && <div className="bhi-modal-error">{error}</div>}
            </div>

            <div className="modal-foot">
              <button type="button" className="btn" onClick={attemptDismiss} disabled={busy}>Cancel</button>
              <button type="submit" className="btn primary" disabled={busy || !scoreValid}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
